/**
 * האבטחה שלי — the ACCOUNT's own security. `/security`.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY A SCREEN OF ITS OWN, AND WHY IT IS NOT UNDER `/tenant`
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Identity belongs to the ACCOUNT, not to a tenant. A platform principal has no tenant at all and
 * still has sessions and an MFA obligation — the platform floor is unconditional and no tenant policy
 * can lower it — so a personal security surface parked under `/tenant` would be unreachable for
 * exactly the accounts the floor is strictest about.
 *
 * That is also why this screen renders on BOTH surfaces rather than answering with `<SurfaceNotice>`.
 * It is the third such address, joining the two MFA screens that already behaved this way, and it is
 * now their parent: enrolment and challenge are steps, and a person looking for "where am I signed
 * in" was previously offered a step with no place to stand.
 *
 * ── WHAT THE SESSION LIST MAY SAY ABOUT A PERSON ────────────────────────────────────────────────
 *
 * How many, since when, and whether it can be ended. No address, no device, no location — the same
 * shape an administrator sees about a colleague, and deliberately the same: the read of a colleague's
 * sessions is audited ALWAYS because it is surveillance of a person, and the way to keep that
 * proportionate is for the payload to hold nothing that would reward it.
 *
 * A revoked session stays in the list with its reason. 02 §8 — no physical delete — and the reason
 * is the point: a row saying *"חשד שההתחברות הגיעה לידיים לא נכונות"* is the only thing that lets
 * somebody ask what was done during it.
 *
 * ── AND "SIGN OUT EVERYWHERE" INCLUDES THIS BROWSER ─────────────────────────────────────────────
 *
 * The route clears the caller's own cookies, on purpose: this is reached by somebody who believes a
 * session of theirs is in the wrong hands, and sparing the one they are using would leave alive the
 * browser they are least sure about. The blast radius says so before the click, which is the whole
 * job of 09 §2.2.
 */

import { useCallback, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { FactList } from '../components/FactList';
import { Modal } from '../components/Modal';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import type { UserSessionSummary } from '../api/contract';
import { mfaPolicyHe } from '../api/adminVocabulary';
import {
  SIGN_OUT_EVERYWHERE_HE,
  sessionRevocationHe,
  sessionRowText,
  sessionSummaryHe,
} from '../api/sessionText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureMfaEnrolment, fixturesEnabled } from '../api/fixtures';
import { ServerRead } from '../components/ServerRead';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'האבטחה שלי';

type Outcome =
  | { readonly kind: 'revoked'; readonly count: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PersonalSecurityScreen(): JSX.Element {
  const ctx = useWorkContext();
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // NO SURFACE CHECK. See the header: identity belongs to the account, and a platform principal has
  // no tenant to be inside. The context bar still shows which surface the session is in.
  /*
   * READ FROM THE SERVER. **Phase 2 §6.**
   *
   * "Where is my account signed in" is a security answer, and it was a fixture — so "sign out
   * everywhere", which IS wired, ended sessions the person had never actually been shown. The list
   * and the revocation now describe the same thing, and the revocation re-reads afterwards so the
   * screen shows what survived rather than what it assumed.
   */
  const read = useCallback(() => callOperation('user.session.read', {}), []);
  const sessionsRead = useServerData(read);
  const sessions: readonly UserSessionSummary[] = listOf(sessionsRead.result, 'sessions');
  const offer = fixturesEnabled() ? fixtureMfaEnrolment : null;
  const back =
    ctx.surface === 'PLATFORM'
      ? { labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }
      : { labelHe: 'ניהול המשרד', href: routes.tenantAdmin };

  async function revokeEverything(): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // Takes NO identifier: the subject is the caller, and there is no value anybody could supply
      // that redirects it at another principal. A write, so it is never retried.
      const result = await callOperation('user.session.revoke_all', { body: {} });
      sessionsRead.reload();
      setOutcome({ kind: 'revoked', count: result.count });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
      setConfirming(false);
      setAcknowledged(false);
    }
  }

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={back}
      provenanceHe="רשימת ההתחברויות מציגה מתי נפתחה כל אחת ומתי נראתה לאחרונה. היא אינה שומרת מיקום, כתובת רשת או פרטי מכשיר."
    >
      <FactList
        facts={[
          { labelHe: 'החשבון', value: ctx.session === null ? null : ctx.session.displayName, absentHe: 'אין חשבון מחובר' },
          {
            labelHe: 'אימות דו-שלבי',
            value: offer === null ? null : mfaPolicyHe(offer.policy),
            absentHe: 'לא התקבלה מדיניות מהשרת',
          },
        ]}
      />

      <div className="actions-row">
        <a className="btn btn--primary" href={routes.mfaEnrolment}>
          רישום אימות דו-שלבי
        </a>
        <a className="btn" href={routes.mfaChallenge}>
          אימות דו-שלבי — הזנת קוד
        </a>
      </div>

      {/*
        THE EMPTY STATE HERE SAID THE WRONG THING, and the conversion is what exposed it.

        It read *"we could not receive the list of sessions from the server"* — under a branch that
        fires when the list is EMPTY. Those are opposite facts. A person checking whether an old
        laptop is still signed in was told the check had failed when it had succeeded and found
        nothing, or told nothing was found when the check had failed. Both readings were available
        from the same sentence, which is worse than either.

        `<ServerRead>` now decides which of the two happened, and each has its own words.
      */}
      <ServerRead result={sessionsRead.result} whatHe="את ההתחברויות של החשבון" skeletonRows={4}>
        {(data) => (
          <>
      <section className="notice" data-tone="info">
        <h2 className="notice__title">איפה החשבון שלך מחובר</h2>
        <p className="notice__body">{sessionSummaryHe(data.sessions)}</p>
        <p className="notice__body">
          התחברות שהסתיימה נשארת ברשימה יחד עם הסיבה. זה מה שמאפשר לברר בדיעבד מה נעשה בה, ולכן היא
          אינה נמחקת.
        </p>
      </section>

      {data.sessions.length === 0 ? (
        <StateBlock
          titleHe="אין התחברויות להצגה"
          // The office's own fact, stated as one: the read SUCCEEDED and there is nothing open.
          bodyHe="השרת ענה, ואין לחשבון הזה אף התחברות רשומה — גם לא כזו שהסתיימה."
          tone="empty"
        />
      ) : (
        <section className="connections">
          {data.sessions.map((session) => {
            const row = sessionRowText(session);
            const revoked =
              session.revokedReason === null ? null : sessionRevocationHe(session.revokedReason);
            return (
              <article key={session.sessionUid} className="connection">
                <header className="connection__head">
                  <h2 className="connection__title">{row.stateHe}</h2>
                  <StatusChip
                    labelHe={row.revocable ? 'פעילה' : 'הסתיימה'}
                    tone={row.tone}
                  />
                </header>
                <p className="connection__explanation">{row.startedHe}</p>
                {row.lastSeenHe === null ? null : (
                  <p className="connection__as-of">{row.lastSeenHe}</p>
                )}
                {row.expiresHe === null ? null : (
                  <p className="connection__fetched">{row.expiresHe}</p>
                )}
                {revoked === null ? null : (
                  // The reason, in words. Seven reasons are seven facts, and this is where the
                  // difference between "a manager ended it" and "somebody suspected a compromise"
                  // reaches the person it happened to.
                  <p className="connection__next-step">{revoked.meaningHe}</p>
                )}
              </article>
            );
          })}
        </section>
      )}
          </>
        )}
      </ServerRead>

      <div className="actions-row">
        <button type="button" className="btn btn--irreversible" onClick={() => setConfirming(true)}>
          {SIGN_OUT_EVERYWHERE_HE.intentHe}
        </button>
      </div>

      {outcome === null ? null : outcome.kind === 'revoked' ? (
        <StateBlock
          titleHe="כל ההתחברויות נותקו"
          bodyHe={`נותקו ${outcome.count} התחברויות, כולל זו שממנה בוצעה הפעולה. נדרשת התחברות מחדש.`}
          nextStep={
            <a className="btn btn--primary" href={routes.signIn}>
              מעבר להתחברות
            </a>
          }
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} />
      )}

      {confirming ? (
        <Modal
          intentHe={SIGN_OUT_EVERYWHERE_HE.intentHe}
          blastRadiusHe={SIGN_OUT_EVERYWHERE_HE.blastRadiusHe}
          fields={1}
          // 09 §2.2: an irreversible action is re-selected explicitly and is never the default focus.
          irreversible
          confirmLabelHe={sending ? 'שולח…' : SIGN_OUT_EVERYWHERE_HE.confirmLabelHe}
          onConfirm={() => {
            if (acknowledged) void revokeEverything();
          }}
          onClose={() => {
            setConfirming(false);
            setAcknowledged(false);
          }}
        >
          <label className="field field--inline">
            <input
              type="checkbox"
              name="acknowledge-sign-out"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.currentTarget.checked)}
            />
            <span className="field__label">הבנתי שגם ההתחברות הנוכחית תסתיים</span>
          </label>
        </Modal>
      ) : null}
    </AppShell>
  );
}
